import React, { useState } from 'react';
import { useAssetTypes, useDeleteAssetType, useDeactivateAssetType, useActivateAssetType } from '../../hooks/useAssetTypes';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import LoadingSpinner from '../common/LoadingSpinner';
import AssetTypeForm from './AssetTypeForm';
import AuditDrawer from '../audit/AuditDrawer';
import type { AssetType } from '../../types/asset';
import { Pencil, Trash2, Archive, CheckCircle, History } from 'lucide-react';

interface AssetTypeListProps {
  onSelectType: (assetType: AssetType) => void;
  selectedTypeId?: string;
}

const AssetTypeList: React.FC<AssetTypeListProps> = ({ onSelectType, selectedTypeId }) => {
  const { data: assetTypes, isLoading } = useAssetTypes(false);
  const deleteAssetType = useDeleteAssetType();
  const deactivateAssetType = useDeactivateAssetType();
  const activateAssetType = useActivateAssetType();
  
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<AssetType | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<AssetType | null>(null);
  const [typeToDeactivate, setTypeToDeactivate] = useState<AssetType | null>(null);
  const [typeToActivate, setTypeToActivate] = useState<AssetType | null>(null);
  const [auditDrawer, setAuditDrawer] = useState<{
    isOpen: boolean;
    assetTypeId: string;
    name: string;
  }>({
    isOpen: false,
    assetTypeId: '',
    name: '',
  });

  const handleCreate = () => {
    setEditingType(null);
    setShowForm(true);
  };

  const handleEdit = (assetType: AssetType) => {
    setEditingType(assetType);
    setShowForm(true);
  };

  const handleDeleteClick = (assetType: AssetType) => {
    setTypeToDelete(assetType);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (typeToDelete) {
      await deleteAssetType.mutateAsync(typeToDelete.assetTypeId);
      setShowDeleteConfirm(false);
      setTypeToDelete(null);
    }
  };

  const handleDeactivateClick = (assetType: AssetType) => {
    setTypeToDeactivate(assetType);
    setShowDeactivateConfirm(true);
  };

  const handleConfirmDeactivate = async () => {
    if (typeToDeactivate) {
      await deactivateAssetType.mutateAsync(typeToDeactivate.assetTypeId);
      setShowDeactivateConfirm(false);
      setTypeToDeactivate(null);
    }
  };

  const handleActivateClick = (assetType: AssetType) => {
    setTypeToActivate(assetType);
    setShowActivateConfirm(true);
  };

  const handleConfirmActivate = async () => {
    if (typeToActivate) {
      console.log('Activating asset type:', typeToActivate.assetTypeId);
      await activateAssetType.mutateAsync(typeToActivate.assetTypeId);
      setShowActivateConfirm(false);
      setTypeToActivate(null);
    }
  };

  const handleViewHistory = (assetType: AssetType) => {
    setAuditDrawer({
      isOpen: true,
      assetTypeId: assetType.assetTypeId,
      name: assetType.name,
    });
  };

  const handleCloseHistory = () => {
    setAuditDrawer({
      isOpen: false,
      assetTypeId: '',
      name: '',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const activeTypes = assetTypes?.filter(t => t.isActive) || [];
  const inactiveTypes = assetTypes?.filter(t => !t.isActive) || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tipos de Activos</h3>
          <p className="text-sm text-gray-600">
            Configura los tipos de activos que tu empresa necesita
          </p>
        </div>
        <Button onClick={handleCreate}>
          + Nuevo Tipo
        </Button>
      </div>

      {activeTypes.length === 0 && inactiveTypes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-600 mb-4">No hay tipos de activos configurados</p>
            <Button onClick={handleCreate}>Crear Primer Tipo</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeTypes.map((assetType) => (
            <Card
              key={assetType.assetTypeId}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedTypeId === assetType.assetTypeId ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => onSelectType(assetType)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{assetType.name}</CardTitle>
                    <CardDescription className="text-sm">
                      {assetType.fields.length} campos configurados
                    </CardDescription>
                  </div>
                  <Badge variant="default">Activo</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm text-gray-600">
                    Campos principales:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {assetType.fields
                      .filter(f => f.isPrimaryDisplay)
                      .map(field => (
                        <Badge key={field.fieldId} variant="outline" className="text-xs">
                          {field.fieldName}
                        </Badge>
                      ))}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewHistory(assetType);
                    }}
                  >
                    <History className="h-3 w-3 mr-1" />
                    Historial
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(assetType);
                    }}
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeactivateClick(assetType);
                    }}
                  >
                    <Archive className="h-3 w-3 mr-1" />
                    Desactivar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {inactiveTypes.length > 0 && (
            <>
              <div className="col-span-full">
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Tipos Desactivados</h4>
                </div>
              </div>
              {inactiveTypes.map((assetType) => (
                <Card
                  key={assetType.assetTypeId}
                  className="opacity-60"
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{assetType.name}</CardTitle>
                        <CardDescription className="text-sm">
                          {assetType.fields.length} campos configurados
                        </CardDescription>
                      </div>
                      <Badge variant="secondary">Desactivado</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewHistory(assetType);
                        }}
                      >
                        <History className="h-3 w-3 mr-1" />
                        Historial
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(assetType);
                        }}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActivateClick(assetType);
                        }}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Activar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(assetType);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {showForm && (
        <AssetTypeForm
          assetType={editingType}
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingType(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-gray-700">
              ¿Estás seguro de que deseas eliminar permanentemente el tipo{' '}
              <span className="font-semibold">{typeToDelete?.name}</span>?
            </p>
            <p className="text-sm text-red-600 text-center">
              Esta acción no se puede deshacer y eliminará todos los activos asociados.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleteAssetType.isLoading}
              >
                {deleteAssetType.isLoading ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={showDeactivateConfirm} onOpenChange={setShowDeactivateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Desactivación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-gray-700">
              ¿Estás seguro de que deseas desactivar el tipo{' '}
              <span className="font-semibold">{typeToDeactivate?.name}</span>?
            </p>
            <p className="text-sm text-gray-600 text-center">
              Podrás reactivarlo más tarde si lo necesitas.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeactivateConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmDeactivate}
                disabled={deactivateAssetType.isLoading}
              >
                {deactivateAssetType.isLoading ? 'Desactivando...' : 'Desactivar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate Confirmation Dialog */}
      <Dialog open={showActivateConfirm} onOpenChange={setShowActivateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Activación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-gray-700">
              ¿Estás seguro de que deseas activar el tipo{' '}
              <span className="font-semibold">{typeToActivate?.name}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowActivateConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmActivate}
                disabled={activateAssetType.isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {activateAssetType.isLoading ? 'Activando...' : 'Activar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Drawer */}
      <AuditDrawer
        entityType="ASSET_TYPE"
        entityId={auditDrawer.assetTypeId}
        entityName={auditDrawer.name}
        isOpen={auditDrawer.isOpen}
        onClose={handleCloseHistory}
      />
    </div>
  );
};

export default AssetTypeList;
